import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCachedResource } from "../hooks/useCachedResource";
import { createStaffPayment, listStaffPayments } from "../api/accounting";
import { listUsers } from "../api/directory";
import { AcctShell } from "../features/acct/AcctShell";
import { useAcctLookups } from "../features/acct/useAcctLookups";
import { DutyEmployees } from "../features/duty/DutyEmployees";
import { NeuButton, NeuCard, NeuInput, NeuSelect } from "../components/neu/Neu";

// المكافآت لها موديول مستقل الآن (/bonuses) — هنا البدلات والسلف فقط.
const KINDS = ["duty_allowance", "advance"];

export default function AcctStaffPayPage() {
  const { t } = useTranslation();
  const { boxes, reload } = useAcctLookups();
  const { data, reload: load } = useCachedResource("acct-staffpay", async () => ({
    rows: await listStaffPayments({}).catch(() => []),
    employees: await listUsers(null, "active"),
  }));
  const rows = data?.rows || [];
  const employees = data?.employees || [];
  const [form, setForm] = useState({ kind: "duty_allowance", amount: "", employee_ids: [], cashbox_id: "", description: "" });

  const toggle = (id) =>
    setForm((prev) => ({
      ...prev,
      employee_ids: prev.employee_ids.includes(id) ? prev.employee_ids.filter((x) => x !== id) : [...prev.employee_ids, id],
    }));

  const save = async () => {
    await createStaffPayment({
      ...form,
      date: new Date().toISOString().slice(0, 10),
      amount: Number(form.amount),
      cashbox_id: form.cashbox_id || boxes[0]?.id,
    });
    toast.success(t("acct.staffPaid"));
    setForm({ ...form, amount: "", employee_ids: [] });
    load();
    reload();
  };

  return (
    <AcctShell titleKey="staffPay">
      <NeuCard className="grid gap-4 p-5" data-testid="staffpay-form">
        <div className="grid gap-3 sm:grid-cols-4">
          <NeuSelect data-testid="staffpay-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {KINDS.map((k) => (
              <option key={k} value={k}>{t(`acct.payKind.${k}`)}</option>
            ))}
          </NeuSelect>
          <NeuInput data-testid="staffpay-amount" type="number" placeholder={t("acct.amountPerEmployee")} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <NeuSelect data-testid="staffpay-box" value={form.cashbox_id} onChange={(e) => setForm({ ...form, cashbox_id: e.target.value })}>
            <option value="">{t("acct.cashbox")}</option>
            {boxes.map((b) => (
              <option key={b.id} value={b.id}>{b.name} ({b.balance})</option>
            ))}
          </NeuSelect>
          <NeuInput data-testid="staffpay-desc" placeholder={t("acct.description")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <DutyEmployees employees={employees} selected={form.employee_ids} onToggle={toggle} />
        <NeuButton variant="primary" data-testid="staffpay-save" disabled={!form.amount || form.employee_ids.length === 0} onClick={save}>
          {t("acct.payAndPost")}
        </NeuButton>
      </NeuCard>

      <div className="grid gap-2" data-testid="staffpay-list">
        {rows.map((row) => (
          <NeuCard key={row.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`staffpay-row-${row.id}`}>
            <span className="font-mono text-xs" dir="ltr">{row.date}</span>
            <span className="min-w-0 flex-1 truncate text-xs font-semibold">{t(`acct.payKind.${row.kind}`)} · {row.employee_ids.length}</span>
            <span className="font-mono text-sm font-bold" dir="ltr">{row.amount}</span>
            <span className="font-mono text-[11px] text-primary" dir="ltr">{row.entry_number}</span>
          </NeuCard>
        ))}
      </div>
    </AcctShell>
  );
}
